import React from 'react';
import {
  TextField,
  NumberInput,
  SelectInput,
  BooleanInput,
  useRecordContext,
  useResourceContext,
  Labeled,
  FormTab,
  TabbedForm,
} from 'react-admin';
import StateInput from 'components/StateInput';
import MarketInput from 'components/MarketInput';
import LocationInput from 'components/LocationInput';
import AgencyInput from 'components/AgencyInput';
import { isEmptyObject } from 'utils/object';
import LocationTypeInput from 'components/LocationTypeInput';
import { Ten99Form, W2LocalForm, W2TravelForm } from './W2And1099SubSettingsForm';
import { useFormContext } from 'react-hook-form';
import useGate from 'hooks/useGate';
import { gateNames } from 'constants/statsig';
import { Alert } from '@mui/material';
import { shouldShowAcknowledgementStatus } from 'utils/acknowledgementHelpers';
import { getInherited, getRateSource } from 'utils/rateSettings/SharedRateSettingsForm.utils';
import { MinimumWageInputs } from './MinimumWageInputs';
import { W2BurdensInputs } from './W2BurdensInputs';
import { FeesInputs } from './FeesSectionInputs';

const renderRateComponent = (isEdit, source) => {
  if (isEdit) {
    if (source === 'market_location_type_id') {
      return (
        <>
          <Labeled>
            <TextField source="market_id" />
          </Labeled>
          <LocationTypeInput source="location_type_id" />
        </>
      );
    } else if (source === 'state_location_type_id') {
      return (
        <>
          <Labeled>
            <TextField source="state_id" />
          </Labeled>
          <LocationTypeInput source="location_type_id" />
        </>
      );
    } else {
      return (
        <Labeled>
          <TextField source={source} />
        </Labeled>
      );
    }
  }

  if (source === 'market_location_type_id') {
    return (
      <>
        <MarketInput source="market_id" />
        <LocationTypeInput source="location_type_id" />
      </>
    );
  } else if (source === 'market_id') {
    return <MarketInput source={source} />;
  } else if (source === 'state_location_type_id') {
    return (
      <>
        <LocationTypeInput source="location_type_id" />
        <StateInput source="state_id" />
      </>
    );
  } else if (source === 'state_id') {
    return <StateInput source={source} />;
  } else if (source === 'location_id') {
    return <LocationInput source={source} />;
  } else if (source === 'agency_id') {
    return (
      <>
        <LocationInput source="location_id" />
        <AgencyInput source="agency_id" />
      </>
    );
  }

  return null;
};

const RateStructureSelect = ({ settingsField, inheritedSettings }) => {
  const { setValue } = useFormContext();

  const onChangeRateStructure = (value) => {
    const selectedValue = value.target.value;
    setValue(`${settingsField}.ten99.use_blended_rate`, selectedValue);
    setValue(`${settingsField}.w2.local.use_blended_rate`, selectedValue);
    setValue(`${settingsField}.w2.travel.use_blended_rate`, selectedValue);
  };

  const inheritedUseBlendedRate = getInherited(inheritedSettings, 'ten99.use_blended_rate');
  const helperTextLabel =
    inheritedUseBlendedRate === 'true'
      ? '12hr rate'
      : inheritedUseBlendedRate === 'false'
      ? '8 hour rate'
      : '';

  return (
    <SelectInput
      source={`${settingsField}.ten99.use_blended_rate`}
      choices={[
        { id: false, name: '8 hour rate' },
        { id: true, name: '12 hour rate' },
      ]}
      label="Rate Structure"
      emptyText="Apply inheritance"
      onChange={onChangeRateStructure}
      helperText={helperTextLabel}
    />
  );
};

const RateSettingsForm = ({ settingsField = null }: { settingsField?: string }) => {
  const record: any = useRecordContext() ?? {};
  const { value: blockCreateRateSettings } = useGate(gateNames.blockCreateNewRateSettings);
  const resource = useResourceContext();
  let isEdit = true;
  if (isEmptyObject(record)) {
    isEdit = false;
  }
  const source = getRateSource(record, resource);
  const inheritedSettings = record?.settings;

  const isLocationLevelRateSetting = resource === 'LocationRateSetting';
  const showAcknowledgementStatus =
    isLocationLevelRateSetting && shouldShowAcknowledgementStatus(record?.location);

  const isStateLevel = resource === 'StateRateSetting';
  const isLocationLevel = resource === 'LocationRateSetting';
  const isW2 = record?.location?.primary_billing_group?.plan === 'enterprise_w2';
  const shouldShowW2TravelTab = isStateLevel || isLocationLevel;
  const w2LocalLabel = shouldShowW2TravelTab ? 'W2 Local' : 'W2';

  if (blockCreateRateSettings) {
    return (
      <Alert severity="warning">
        You cannot create or edit rate settings today due a Fintech team deployment. If you have any
        questions, please reach out using the software_support Slack channel.
      </Alert>
    );
  }

  return (
    <TabbedForm>
      <FormTab label="General">
        {renderRateComponent(isEdit, source)}
        <BooleanInput label="Active" source="active" defaultValue={true} />
        {isLocationLevelRateSetting && (
          <RateStructureSelect
            inheritedSettings={inheritedSettings}
            settingsField={settingsField}
          />
        )}
        {showAcknowledgementStatus && (
          <SelectInput
            source={`${settingsField}.rate_structure_acknowledgement_status`}
            choices={[
              { id: 'pending', name: 'Pending', disabled: true },
              { id: 'acknowledged', name: 'Acknowledged' },
              { id: 'not_required', name: 'Not Required', disabled: true },
            ]}
            label="Acknowledgement Status"
            helperText={getInherited(inheritedSettings, 'rate_structure_acknowledgement_status')}
          />
        )}
        <SelectInput
          label="Track Unpaid Breaks"
          source={`${settingsField}.track_unpaid_breaks`}
          choices={[
            { id: true, name: 'True' },
            { id: false, name: 'False' },
          ]}
          helperText={getInherited(inheritedSettings, 'track_unpaid_breaks')}
        />
        <NumberInput
          label="Other burden multiplier"
          source={`${settingsField}.other_burden_multiplier`}
          helperText={getInherited(inheritedSettings, 'other_burden_multiplier')}
        />

        {isStateLevel && (
          <MinimumWageInputs settingsField={settingsField} inheritedSettings={inheritedSettings} />
        )}

        {(isStateLevel || isLocationLevel) && (
          <W2BurdensInputs settingsField={settingsField} inheritedSettings={inheritedSettings} />
        )}

        {isStateLevel && (
          <FeesInputs settingsField={settingsField} inheritedSettings={inheritedSettings} />
        )}
      </FormTab>

      <FormTab label="1099" disabled={isW2 && isLocationLevel}>
        <Ten99Form source={settingsField} inheritedSettings={inheritedSettings} showTitle={false} />
      </FormTab>

      <FormTab label={w2LocalLabel}>
        <W2LocalForm
          source={settingsField}
          inheritedSettings={inheritedSettings}
          showTitle={false}
        />
      </FormTab>

      {shouldShowW2TravelTab && (
        <FormTab label="W2 Travel">
          <W2TravelForm
            source={settingsField}
            inheritedSettings={inheritedSettings}
            showTitle={false}
          />
        </FormTab>
      )}
    </TabbedForm>
  );
};

export default RateSettingsForm;
